import { useState, useEffect } from "react";
import { Box, Typography, Button, FormControlLabel, Checkbox } from "@mui/material";
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import theme from "../styles/theme";

interface User {
  id: number;
  username: string;
  nom_complet?: string;
}

interface Category {
  id: number;
  name: string;
}

interface PlayerRadarComparisonProps {
  players: User[];
  categories: Category[];
  specialCategories: Array<{ id: string; name: string; icon: string }>;
  computePlayerCategoryRates: (playerId: number) => { [cid: number]: { rate: number; count: number } };
  computeSpecialRates: (
    playerId: number,
    category: "mobilite" | "puissance" | "construction" | "agressivite"
  ) => { rate: number; count: number };
  specialAssignments: { [exerciseId: number]: { [key: string]: boolean } };
}

export default function PlayerRadarComparison({
  players,
  categories,
  specialCategories,
  computePlayerCategoryRates,
  computeSpecialRates,
  specialAssignments,
}: PlayerRadarComparisonProps) {
  const [selectedPlayers, setSelectedPlayers] = useState<number[]>([]);

  useEffect(() => {
    setSelectedPlayers(players.map((p) => p.id));
  }, [players]);

  const handleLegendToggle = (playerId: number) => {
    setSelectedPlayers((prev) =>
      prev.includes(playerId) ? prev.filter((id) => id !== playerId) : [...prev, playerId]
    );
  };

  const handleShowAll = () => {
    setSelectedPlayers(players.map((p) => p.id));
  };

  const handleSelectAll = () => {
    setSelectedPlayers(players.map((p) => p.id));
  };

  const handleDeselectAll = () => {
    setSelectedPlayers([]);
  };

  const isMobile = window.innerWidth < 600;

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const sortedPayload = [...payload].sort((a, b) => b.value - a.value);

      return (
        <Box
          sx={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            p: 1.5,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontWeight: "bold",
              mb: 1,
              color: theme.palette.text.primary,
            }}
          >
            {payload[0].payload.category}
          </Typography>
          {sortedPayload.map((entry: any, index: number) => (
            <Typography
              key={index}
              variant="body2"
              sx={{
                color: entry.color,
                fontSize: "0.875rem",
              }}
            >
              {entry.name}: {entry.value}%
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const radarData = (() => {
    const dataPoints = categories
      .map((cat) => {
        const dataPoint: any = { category: cat.name };
        let hasNonZeroValue = false;

        players.forEach((player) => {
          const playerRates = computePlayerCategoryRates(player.id);
          const rate = playerRates[cat.id]?.rate ?? 0;
          dataPoint[player.nom_complet || player.username] = rate;
          if (rate > 0) hasNonZeroValue = true;
        });

        return hasNonZeroValue ? dataPoint : null;
      })
      .filter(Boolean);

    specialCategories.forEach((special) => {
      const totalExercises = Object.values(specialAssignments).filter(
        (assignments) => assignments[special.id as keyof typeof assignments]
      ).length;

      if (totalExercises > 0) {
        const dataPoint: any = { category: `${special.icon} ${special.name}` };
        let hasNonZeroValue = false;

        players.forEach((player) => {
          const result = computeSpecialRates(
            player.id,
            special.id as "mobilite" | "puissance" | "construction" | "agressivite"
          );
          dataPoint[player.nom_complet || player.username] = result.rate;
          if (result.rate > 0) hasNonZeroValue = true;
        });

        if (hasNonZeroValue) {
          dataPoints.push(dataPoint);
        }
      }
    });

    return dataPoints;
  })();

  const colors = [
    "#0B6EA5",
    "#ff8001",
    "#4caf50",
    "#f44336",
    "#9c27b0",
    "#ff9800",
    "#2196f3",
    "#e91e63",
    "#009688",
    "#795548",
  ];

  return (
    <Box
      sx={{
        borderRadius: theme.shape.borderRadius,
        background: theme.palette.background.paper,
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
        p: 3,
        mb: 4,
        minHeight: isMobile ? 800 : 20,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          variant="h3"
          sx={{
            fontSize: "1.3rem",
            fontWeight: "bold",
            color: theme.palette.primary.main,
          }}
        >
          Comparaison globale des profils
        </Typography>
        {selectedPlayers.length !== players.length && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleShowAll}
            sx={{
              textTransform: "none",
            }}
          >
            Afficher toutes les joueuses
          </Button>
        )}
      </Box>
      <ResponsiveContainer width="100%" height={isMobile ? 550 : 900}>
        <RadarChart
          data={radarData}
          margin={{
            top: -50,
            right: 30,
            bottom: isMobile ? 60 : 60,
            left: 30,
          }}
        >
          <PolarGrid />
          <PolarAngleAxis dataKey="category" />
          <PolarRadiusAxis domain={[0, 100]} />
          <Tooltip content={<CustomTooltip />} />
          {players.map((player, idx) => {
            const color = colors[idx % colors.length];
            const isVisible = selectedPlayers.includes(player.id);

            return (
              <Radar
                key={player.id}
                name={player.nom_complet || player.username}
                dataKey={player.nom_complet || player.username}
                stroke={color}
                fill={color}
                fillOpacity={isVisible ? 0.25 : 0}
                strokeWidth={isVisible ? 2 : 0}
                hide={!isVisible}
              />
            );
          })}
        </RadarChart>
      </ResponsiveContainer>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mt: 3,
          pb: isMobile ? 2 : 0,
          maxHeight: 110,
          overflowY: "auto",
        }}
      >
        {players.map((player, idx) => (
          <FormControlLabel
            key={player.id}
            control={
              <Checkbox
                checked={selectedPlayers.includes(player.id)}
                onChange={() => handleLegendToggle(player.id)}
                sx={{
                  color: colors[idx % colors.length],
                  "&.Mui-checked": {
                    color: colors[idx % colors.length],
                  },
                }}
              />
            }
            label={player.nom_complet || player.username}
            sx={{
              minWidth: isMobile ? 120 : "auto",
              ".MuiTypography-root": {
                color: colors[idx % colors.length],
                fontWeight: selectedPlayers.includes(player.id)
                  ? "bold"
                  : "normal",
                fontSize: isMobile ? "0.95rem" : "1rem",
              },
            }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleSelectAll}
          sx={{ textTransform: "none" }}
        >
          Tout sélectionner
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleDeselectAll}
          sx={{ textTransform: "none" }}
        >
          Tout désélectionner
        </Button>
      </Box>
    </Box>
  );
}
